// src/components/tools/nintendo-quest/NintendoThermometer.tsx

import React from 'react';
import { motion } from 'framer-motion';
import { GamepadIcon, DollarSign } from 'lucide-react';

interface NintendoThermometerProps {
  currentPoints: number;
  targetPoints: number;
  moneyEquivalent: {
    earned: number;
    existing: number;
    total: number;
    needed: number;
  };
}

export default function NintendoThermometer({ 
  currentPoints, 
  targetPoints, 
  moneyEquivalent 
}: NintendoThermometerProps) {
  const progressPercentage = Math.min((currentPoints / targetPoints) * 100, 100);
  
  // Thermometer milestones
  const milestones = [0, 100, 200, 300, 400, 500];
  
  // Color based on progress
  const getProgressColor = (percentage: number) => {
    if (percentage < 25) return 'from-red-500 to-red-600';
    if (percentage < 50) return 'from-orange-500 to-orange-600';
    if (percentage < 75) return 'from-yellow-500 to-yellow-600';
    return 'from-green-500 to-green-600';
  };

  const progressColor = getProgressColor(progressPercentage);

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <GamepadIcon className="w-6 h-6 text-blue-400" />
          Nintendo Switch Progress
        </h3>
        <div className="text-right">
          <div className="text-2xl font-bold text-white">{currentPoints}</div>
          <div className="text-sm text-white/60">of {targetPoints}</div>
        </div>
      </div>

      {/* Thermometer Container */}
      <div className="relative">
        {/* Background thermometer */}
        <div className="w-16 h-80 mx-auto bg-white/20 rounded-full relative overflow-hidden border border-white/30">
          {/* Progress fill */}
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: `${progressPercentage}%` }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t ${progressColor} rounded-full`}
          />
          
          {/* Thermometer bulb */}
          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-full border-2 border-white/30" />
        </div>

        {/* Milestone markers and labels */}
        <div className="absolute left-20 top-0 h-80">
          {milestones.map((milestone, index) => {
            const position = 100 - (milestone / targetPoints) * 100;
            const isReached = currentPoints >= milestone;
            
            return (
              <div
                key={milestone}
                className="absolute flex items-center"
                style={{ top: `${position}%`, transform: 'translateY(-50%)' }}
              >
                {/* Marker line */}
                <div className={`w-8 h-0.5 ${isReached ? 'bg-white' : 'bg-white/30'}`} />
                
                {/* Label */}
                <div className={`ml-3 text-sm font-medium ${isReached ? 'text-white' : 'text-white/50'}`}>
                  {milestone}
                </div>
                
                {/* Achievement indicator */}
                {isReached && milestone > 0 && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="ml-2 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center"
                  >
                    <span className="text-xs">✓</span>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Money equivalent */}
      <div className="mt-4 p-4 bg-white/10 rounded-xl border border-white/20">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-semibold text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            Money Progress
          </h4>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-white/70">Points Earned:</span>
            <span className="text-green-400 font-bold">${moneyEquivalent.earned.toFixed(0)}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-white/70">Your Savings:</span>
            <span className="text-blue-400 font-bold">${moneyEquivalent.existing}</span>
          </div>
          
          <div className="border-t border-white/20 pt-2">
            <div className="flex justify-between items-center">
              <span className="text-white font-semibold">Total Available:</span>
              <span className="text-white font-bold text-lg">${moneyEquivalent.total.toFixed(0)}</span>
            </div>
          </div>
          
          <div className="flex justify-between items-center text-sm">
            <span className="text-white/70">Nintendo Switch Goal:</span>
            <span className="text-white/70">${moneyEquivalent.needed}</span>
          </div>
          
          {moneyEquivalent.total < moneyEquivalent.needed && (
            <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-3 mt-3">
              <div className="text-red-300 font-semibold text-center">
                💰 Need ${(moneyEquivalent.needed - moneyEquivalent.total).toFixed(0)} more!
              </div>
              <div className="text-red-400/70 text-md text-center mt-1">
                That's {Math.ceil((moneyEquivalent.needed - moneyEquivalent.total) * 2)} more points
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}